// Captures waiting for the owner (2026-09-24): nothing a capture says is done
// until the owner says so. Each shows who sent it and their words, what Prime
// suggests (every change a line to untick), and four ways on: do it, talk it
// over with Prime, keep it as a plain inbox task, or remove it. Undo covers
// each. They live at the top of the Inbox; the push on arrival opens it.
import { PrimeMark } from '@/components/PrimeMark'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Check, KeyRound, MessageCircle, Trash2 } from 'lucide-react'
import { useNames, usePeople, useResource, useSuggestions } from '@/db/hooks'
import type { SuggestionResource } from '@/kinds/suggestion'
import type { TaskResource } from '@/kinds/task/apply'
import type { TaskSpec, TaskStatus } from '@/kinds/task/schema'
import { acceptSuggestion, describeOp, dismissSuggestion } from '@/lib/suggestions'
import { firstOf, personFor } from '@/lib/people'
import { fmtRemind } from './RemindPicker'
import { dropCapture } from '@/lib/captures'
import { Avatar } from './PersonBits'
import { Button, SectionTitle, cx } from './ui'

/** The sender's own words: what they sent, without the owner's note for Prime. */
export const senderWords = (notes?: string) => {
  const i = (notes ?? '').lastIndexOf('Context from Alfie:')
  return (i >= 0 ? (notes ?? '').slice(0, i) : (notes ?? '')).trim()
}

/** Pending capture suggestions, newest first. */
export const useWaitingCaptures = (): SuggestionResource[] =>
  useSuggestions().filter((s) => s.spec.type === 'capture')

/** Pending writes an API token asked for (2026-09-29), newest first. */
export const useWaitingRequests = (): SuggestionResource[] =>
  useSuggestions().filter((s) => s.spec.type === 'api')

export const WaitingCaptures = () => {
  const waiting = useWaitingCaptures()
  const asked = useWaitingRequests()
  if (!waiting.length && !asked.length) return null
  return (
    <section className="mb-5" data-testid="waiting-captures">
      <SectionTitle>Waiting for you</SectionTitle>
      <div className="flex flex-col gap-3">
        {asked.map((s) => (
          <RequestCard key={s.id} s={s} />
        ))}
        {waiting.map((s) => (
          <WaitingCard key={s.id} s={s} />
        ))}
      </div>
    </section>
  )
}

/**
 * A write an outside app asked for through its API token: which app, what it
 * would change (each line tickable), and do it or turn it down. The app is
 * told nothing either way; it sees the result the next time it reads.
 */
const RequestCard = ({ s }: { s: SuggestionResource }) => {
  const { titleOf, kindOf } = useNames()
  const [kept, setKept] = useState<Set<number>>(() => new Set(s.spec.ops.map((_, i) => i)))
  const [busy, setBusy] = useState(false)
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }
  const toggle = (i: number) =>
    setKept((k) => {
      const n = new Set(k)
      if (n.has(i)) n.delete(i)
      else n.add(i)
      return n
    })
  return (
    <article className="card overflow-hidden" data-testid="waiting-request">
      <header className="flex items-start gap-3 px-3.5 pt-3">
        <span className="h-8 w-8 shrink-0 rounded-full bg-raised inline-flex items-center justify-center text-muted">
          <KeyRound size={15} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-muted">
            {s.labels.from ?? 'An app'} asks
            <span className="text-faint"> {fmtRemind(s.created_at)}</span>
          </p>
          <ul className="mt-0.5">
            {s.spec.ops.map((op, i) => (
              <li key={i}>
                <label className="flex items-start gap-2 text-[15px] leading-snug py-1 cursor-pointer">
                  {s.spec.ops.length > 1 && (
                    <input
                      type="checkbox"
                      checked={kept.has(i)}
                      onChange={() => toggle(i)}
                      className="mt-1"
                    />
                  )}
                  <span className={cx(!kept.has(i) && 'text-faint line-through')}>
                    {describeOp(op, titleOf, kindOf)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </header>
      <footer className="flex flex-wrap items-center gap-1.5 px-3.5 py-3 mt-2 border-t border-line/70">
        <Button
          variant="primary"
          disabled={busy || kept.size === 0}
          onClick={() =>
            void run(() =>
              acceptSuggestion(
                s,
                [...kept].sort((a, b) => a - b),
              ),
            )
          }
          data-testid="request-do"
        >
          <Check size={15} /> Do it
        </Button>
        <Button
          variant="chip"
          disabled={busy}
          onClick={() => void run(() => dismissSuggestion(s))}
          data-testid="request-decline"
        >
          Not now
        </Button>
      </footer>
    </article>
  )
}

const WaitingCard = ({ s }: { s: SuggestionResource }) => {
  const navigate = useNavigate()
  const people = usePeople()
  const { titleOf, kindOf } = useNames()
  const carrier = useResource<TaskSpec, TaskStatus>(s.spec.target_id) as TaskResource | undefined
  const [kept, setKept] = useState<Set<number>>(() => new Set(s.spec.ops.map((_, i) => i)))
  const [busy, setBusy] = useState(false)
  const from = s.labels.from
  const sender = personFor(people, from)
  const who = sender ? firstOf(sender) : from ? from : 'You'
  const words = carrier ? carrier.spec.title : s.spec.prompt?.split('\n')[0]
  const details = senderWords(carrier?.spec.notes)
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }
  const toggle = (i: number) =>
    setKept((k) => {
      const n = new Set(k)
      if (n.has(i)) n.delete(i)
      else n.add(i)
      return n
    })
  return (
    <article className="card overflow-hidden" data-testid="waiting-capture">
      <header className="flex items-start gap-3 px-3.5 pt-3">
        {sender ? (
          <Avatar person={sender} size={32} />
        ) : (
          <span className="h-8 w-8 shrink-0 rounded-full bg-raised inline-flex items-center justify-center text-muted">
            <PrimeMark size={15} aria-hidden />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-muted">
            {from ? `${who} sent` : 'You captured'}
            <span className="text-faint"> {fmtRemind(s.created_at)}</span>
          </p>
          <p className="text-[15px] leading-snug">{words}</p>
          {details && <p className="text-[13px] text-fg-2 whitespace-pre-line mt-1">{details}</p>}
        </div>
      </header>
      <div className="px-3.5 pt-3">
        <p className="text-[13px] font-semibold text-muted flex items-center gap-1.5">
          <PrimeMark size={13} className="text-accent-text" aria-hidden /> Prime suggests
        </p>
        {s.spec.rationale && <p className="text-[13px] text-faint">{s.spec.rationale}</p>}
        <ul className="mt-1.5">
          {s.spec.ops.map((op, i) => (
            <li key={i}>
              <label className="flex items-start gap-2 text-sm py-1.5 cursor-pointer">
                <input type="checkbox" checked={kept.has(i)} onChange={() => toggle(i)} className="mt-0.5" />
                <span className={cx(!kept.has(i) && 'text-faint line-through')}>
                  {describeOp(op, titleOf, kindOf)}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <footer className="flex flex-wrap items-center gap-1.5 px-3.5 py-3 mt-1 border-t border-line/70">
        <Button
          variant="primary"
          disabled={busy || kept.size === 0}
          onClick={() =>
            void run(() =>
              acceptSuggestion(
                s,
                [...kept].sort((a, b) => a - b),
              ),
            )
          }
          data-testid="capture-do"
        >
          <Check size={15} /> Do it
        </Button>
        <Button
          variant="chip"
          onClick={() =>
            void navigate({
              to: '/agents',
              search: { ask: `About ${from ? `${who}’s` : 'my'} “${words ?? ''}”: ` } as never,
            })
          }
          data-testid="capture-talk"
        >
          <MessageCircle size={14} /> Talk to Prime
        </Button>
        <Button
          variant="chip"
          disabled={busy}
          onClick={() => void run(() => dismissSuggestion(s))}
          data-testid="capture-keep"
        >
          Keep it
        </Button>
        <span className="flex-1" />
        <Button
          variant="quiet"
          disabled={busy}
          onClick={() =>
            void run(() =>
              carrier && !carrier.deleted_at
                ? dropCapture(carrier.id, carrier.spec.title)
                : dismissSuggestion(s),
            )
          }
          aria-label="Remove it"
          title="Remove it, and anything it made"
          data-testid="capture-remove"
        >
          <Trash2 size={15} />
        </Button>
      </footer>
    </article>
  )
}
